/**
 * This module handles the creation of upload channels for Firebase Storage operations.
 * It provides a redux-saga compatible channel that emits progress, success, and error events
 * during file uploads.
 */

import { EventChannel, eventChannel, END } from "redux-saga";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { storage } from "../../../config/firebase";

/**
 * Creates an upload channel for a file using Firebase Storage.
 *
 * @param userId - The ID of the user uploading the file
 * @param file - The file to be uploaded
 * @returns EventChannel that emits upload events:
 *   - progress: Upload progress percentage
 *   - success: Upload completion with download URL
 *   - error: Upload failure with error message
 */
export function createUploadChannel(
  userId: string,
  file: File
): EventChannel<any> {
  return eventChannel((emitter) => {
    const documentId = uuidv4();

    const sanitizedFileName = file.name.replace(/\//g, ":");

    const storageRef = ref(
      storage,
      `documents/${userId}/${documentId}/${sanitizedFileName}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Setup upload observers
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        emitter({
          type: "progress",
          payload: { fileName: file.name, progress },
        });
      },
      (error) => {
        emitter({ type: "error", payload: error.message });
        emitter(END);
      },
      async () => {
        try {
          const url = await getDownloadURL(storageRef);
          emitter({
            type: "success",
            payload: {
              id: documentId,
              filename: file.name,
              url,
            },
          });
          emitter(END);
        } catch (error) {
          emitter({
            type: "error",
            payload: error instanceof Error ? error.message : "Upload failed",
          });
          emitter(END);
        }
      }
    );

    // Return unsubscribe function
    return () => {
      uploadTask.cancel();
    };
  });
}
